import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { isUndefined } from 'lodash'
import { Payload } from 'recharts/types/component/DefaultTooltipContent'
import { CostSavingChartData } from 'src/Libs/dynamic-power-prices/types'
import { getLocalizedCostSavingChartValues } from 'src/Libs/dynamic-power-prices/utils'
import { getLocalizedNumber } from 'src/Libs/languages/functions/default'
import { UnitTypes } from 'src/Libs/types'
import { useCostSavingChartColorSettings } from './use-cost-saving-chart-color-settings'

type UseTooltipItemValues = {
  consumptionPriceColor: string
  consumptionPriceValue: string
  threeSixtyBatteryColor: string
  threeSixtyBatteryProducedEnergy: string
  threeSixtyBatterySavings: string
  threeSixtyWallboxColor: string
  threeSixtyWallboxProducedEnergy: string
  threeSixtyWallboxSavings: string
  summedSavings: string
}

/**
 * Hook to get the values for the tooltip of the cost savings chart.
 *
 * TODO: Clean up this hook for better readability if you touch it next and their is some buffer time in the sprint.
 */
const useTooltipItemValues = ({ payload }: { payload: Payload<number, keyof CostSavingChartData>[] }): UseTooltipItemValues => {
  const { t } = useCustomTranslation()
  const chartColors = useCostSavingChartColorSettings()
  const notApplicableText = t('common.notAvailable')
  const unit = UnitTypes.kiloWattHour

  const consumptionPricePayload = payload?.filter(item => item?.name === 'consumptionPrice')[0]
  const hasConsumptionPrice = consumptionPricePayload?.value !== undefined

  const threeSixtyBatteryPayload = payload?.filter(item => item?.name === 'threeSixtyBatterySavings')[0]
  const threeSixtyBatterySavings = threeSixtyBatteryPayload?.payload?.threeSixtyBatterySavings || 0
  const threeSixtyBatteryEnergy = threeSixtyBatteryPayload?.payload?.threeSixtyBatteryProducedEnergy || 0

  const threeSixtyWallboxPayload = payload?.filter(item => item?.name === 'threeSixtyWallboxSavings')[0]
  const threeSixtyWallboxSavings = threeSixtyWallboxPayload?.payload?.threeSixtyWallboxSavings || 0
  const threeSixtyWallboxEnergy = threeSixtyWallboxPayload?.payload?.threeSixtyWallboxProducedEnergy || 0

  const { localizedEnergy: threeSixtyBatteriesLocalizedEnergy, localizedSavings: threeSixtyBatteriesLocalizedSavings } =
    getLocalizedCostSavingChartValues(threeSixtyBatteryEnergy, threeSixtyBatterySavings)
  const { localizedEnergy: threeSixtyWallboxLocalizedEnergy, localizedSavings: threeSixtyWallboxLocalizedSavings } =
    getLocalizedCostSavingChartValues(threeSixtyWallboxEnergy, threeSixtyWallboxSavings)

  const formattedConsumptionPriceValue = hasConsumptionPrice
    ? `${Number(Number(consumptionPricePayload?.value).toFixed(0)) + 0} ${UnitTypes.centsPerKiloWattHour}` // add +0 here to avoid "-0"
    : notApplicableText

  const formattedWallboxProducedEnergy = `${threeSixtyWallboxLocalizedEnergy} ${unit}`
  const formattedWallboxSavings = !isUndefined(threeSixtyWallboxSavings)
    ? `${threeSixtyWallboxLocalizedSavings} ${UnitTypes.euros}`
    : notApplicableText

  const formattedBatteryProducedEnergy = `${threeSixtyBatteriesLocalizedEnergy} ${unit}`
  const formattedBatterySavings = !isUndefined(threeSixtyBatterySavings)
    ? `${threeSixtyBatteriesLocalizedSavings} ${UnitTypes.euros}`
    : notApplicableText

  const summedSavings =
    (parseFloat(threeSixtyBatteriesLocalizedSavings.replace(',', '.')) || 0) + (parseFloat(threeSixtyWallboxLocalizedSavings.replace(',', '.')) || 0)
  const formattedSummedSavings = `${getLocalizedNumber(summedSavings, 2)} ${UnitTypes.euros}`

  return {
    consumptionPriceColor: chartColors.consumptionPriceColor,
    consumptionPriceValue: formattedConsumptionPriceValue,
    threeSixtyBatteryColor: chartColors.threeSixtySavingsBatteryColor,
    threeSixtyBatteryProducedEnergy: formattedBatteryProducedEnergy,
    threeSixtyBatterySavings: formattedBatterySavings,
    threeSixtyWallboxColor: chartColors.threeSixtySavingsWallboxColor,
    threeSixtyWallboxProducedEnergy: formattedWallboxProducedEnergy,
    threeSixtyWallboxSavings: formattedWallboxSavings,
    summedSavings: formattedSummedSavings,
  }
}

export default useTooltipItemValues
